// =============================================================================
// Mobile Sidebar - Full-screen drawer sidebar for mobile (Lapyno)
// =============================================================================

import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Users,
  Settings,
  Shield,
  LogOut,
  Building2,
  Star,
  FolderKanban,
  UserPlus,
  ArrowLeftRight,
  ChevronsUp,
  LayoutDashboard,
  CalendarDays,
  Sparkles,
  FileText,
  MessageCircle,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useFavorites } from '@/hooks/useFavorites';
import { OrganizationSwitcherDialog } from './OrganizationSwitcherDialog';
import { toast } from 'sonner';

const LAST_PROJECT_KEY = 'lapyno_last_project_id';

function getProjectsPath(): string {
  return '/projects';
}

interface MobileSidebarProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// Module items for mobile
const MOBILE_MODULES = [
  { id: 'dashboard', icon: LayoutDashboard, label: 'Tableau de bord', path: '/dashboard', color: '#10B981' },
  { id: 'projects', icon: FolderKanban, label: 'Projets', getPath: getProjectsPath, color: '#3B82F6' },
  { id: 'planning', icon: CalendarDays, label: 'Planification', path: '/planning', color: '#F59E0B' },
  { id: 'ai', icon: Sparkles, label: 'IA', path: '/ai', color: '#8B5CF6', comingSoon: true },
  { id: 'teams', icon: Users, label: 'Équipes', path: '/teams', color: '#EC4899' },
  { id: 'documents', icon: FileText, label: 'Documents', path: '/documents', color: '#06B6D4', comingSoon: true },
  { id: 'discussion', icon: MessageCircle, label: 'Discussion', path: '/discussion', color: '#EF4444', comingSoon: true },
];

export function MobileSidebar({ open, onOpenChange }: MobileSidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { profile, signOut } = useAuth();
  const { currentOrganization } = useOrganization();
  const { favorites } = useFavorites();
  const [showOrgSwitcher, setShowOrgSwitcher] = useState(false);

  const handleNavigate = (path: string) => {
    navigate(path);
    onOpenChange(false);
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/auth');
  };

  const handleModuleClick = (module: typeof MOBILE_MODULES[0]) => {
    if (module.comingSoon) {
      toast.info('Bientôt disponible', {
        description: `${module.label} sera disponible prochainement.`
      });
    } else {
      const path = module.getPath ? module.getPath() : module.path;
      handleNavigate(path!);
    }
  };

  const isModuleActive = (module: typeof MOBILE_MODULES[0]) => {
    if (module.id === 'projects') {
      return location.pathname.startsWith('/projects') || location.pathname === '/admin/access';
    }
    return module.path && location.pathname.startsWith(module.path);
  };

  const getInitials = (name: string | null) => {
    if (!name) return 'U';
    return name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="w-[85vw] max-w-sm p-0 flex flex-col">
          {/* Header with user info */}
          <SheetHeader className="p-4 border-b">
            <div className="flex items-center gap-3">
              <Avatar className="h-12 w-12">
                <AvatarImage src={profile?.avatar_url || undefined} />
                <AvatarFallback className="bg-primary text-primary-foreground">
                  {getInitials(profile?.full_name)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <SheetTitle className="text-left text-base truncate">
                  {profile?.full_name || 'Utilisateur'}
                </SheetTitle>
                <p className="text-sm text-muted-foreground truncate">
                  {profile?.email}
                </p>
              </div>
            </div>
            {currentOrganization && (
              <button 
                onClick={() => {
                  onOpenChange(false);
                  setShowOrgSwitcher(true);
                }}
                className="flex items-center gap-2 mt-3 p-2 rounded-lg bg-muted/50 hover:bg-muted transition-colors w-full text-left"
              >
                <Building2 className="h-4 w-4 text-muted-foreground shrink-0" />
                <span className="text-sm font-medium truncate flex-1">{currentOrganization.name}</span>
                <ArrowLeftRight className="h-4 w-4 text-muted-foreground shrink-0" />
              </button>
            )}
          </SheetHeader>

          <ScrollArea className="flex-1">
            <div className="p-4 space-y-6">
              {/* Modules Section */}
              <div>
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                  Modules
                </h3>
                <div className="space-y-1">
                  {MOBILE_MODULES.map((module) => {
                    if (!module?.icon) return null;
                    const Icon = module.icon;
                    const isActive = isModuleActive(module);
                    return (
                      <button
                        key={module.id}
                        onClick={() => handleModuleClick(module)}
                        className={cn(
                          "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors",
                          isActive
                            ? "font-medium"
                            : "text-foreground hover:bg-muted"
                        )}
                        style={{
                          backgroundColor: isActive ? `${module.color}15` : undefined,
                          color: isActive ? module.color : undefined,
                        }}
                      >
                        <div 
                          className="w-8 h-8 rounded-lg flex items-center justify-center"
                          style={{ 
                            backgroundColor: `${module.color}20`,
                          }}
                        >
                          <Icon className="h-4 w-4" style={{ color: module.color }} />
                        </div>
                        <span className="flex-1 text-left">{module.label}</span>
                        {module.comingSoon && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                            Bientôt
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              <Separator />

              {/* Favorites */}
              {favorites.length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                    Favoris
                  </h3>
                  <div className="space-y-1">
                    {favorites.slice(0, 5).map((fav) => (
                      <button
                        key={fav.project_id}
                        onClick={() => handleNavigate(`/projects/${fav.project_id}`)}
                        className={cn(
                          "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors",
                          location.pathname === `/projects/${fav.project_id}`
                            ? "bg-[hsl(var(--module-primary)/0.1)] text-[hsl(var(--module-primary))] font-medium"
                            : "text-foreground hover:bg-muted"
                        )}
                      >
                        <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                        <span className="truncate">{fav.project_name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <Separator />

              {/* Admin Section */}
              <div>
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                  Workspace
                </h3>
                <div className="space-y-1">
                  <button
                    onClick={() => handleNavigate('/admin/access?tab=members')}
                    className={cn(
                      "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors",
                      location.pathname === '/admin/access'
                        ? "font-medium bg-red-500/10 text-red-500"
                        : "text-foreground hover:bg-muted"
                    )}
                  >
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-red-500/20">
                      <Users className="h-4 w-4 text-red-500" />
                    </div>
                    <span>Membres</span>
                  </button>
                  
                  {/* Rôles masqué pour l'instant */}

                  <button
                    onClick={() => handleNavigate('/admin/settings')}
                    className={cn(
                      "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors",
                      location.pathname === '/admin/settings'
                        ? "font-medium bg-red-500/10 text-red-500"
                        : "text-foreground hover:bg-muted"
                    )}
                  >
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-red-500/20">
                      <Settings className="h-4 w-4 text-red-500" />
                    </div>
                    <span>Paramètres</span>
                  </button>

                  <button
                    onClick={() => handleNavigate('/admin/access?tab=invitations')}
                    className={cn(
                      "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors",
                      "text-foreground hover:bg-muted"
                    )}
                  >
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-blue-500/20">
                      <UserPlus className="h-4 w-4 text-blue-500" />
                    </div>
                    <span>Inviter une personne</span>
                  </button>
                </div>
              </div>

              <Separator />

              {/* Upgrade */}
              <button
                onClick={() => {
                  toast.info('Bientôt disponible', {
                    description: 'La page des forfaits sera disponible prochainement.'
                  });
                }}
                className="w-full flex items-center gap-3 px-3 py-3 rounded-xl text-sm transition-all hover:scale-[1.02]"
                style={{
                  background: 'linear-gradient(135deg, #8B5CF6 0%, #6366F1 50%, #0EA5E9 100%)',
                }}
              >
                <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-white/20">
                  <ChevronsUp className="h-4 w-4 text-white" />
                </div>
                <span className="text-white font-medium">Mettre à niveau</span>
              </button>
            </div>
          </ScrollArea>

          {/* Footer */}
          <div className="p-4 border-t mt-auto">
            <button
              onClick={handleSignOut}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-destructive hover:bg-destructive/10 transition-colors"
            >
              <LogOut className="h-5 w-5" />
              <span>Déconnexion</span>
            </button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Organization Switcher Dialog */}
      <OrganizationSwitcherDialog
        open={showOrgSwitcher}
        onOpenChange={setShowOrgSwitcher}
      />
    </>
  );
}
